import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { injectHotkeyManagerService } from '@libs/shared/hotkey/hotkey-manager.service';
import { allAllowInElements } from '@libs/shared/utils/const/shortcut';
import {
  base64ToFile,
  ImageCroppedEvent,
  ImageTransform,
} from 'ngx-image-cropper';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-profile-image-cropper-dialog',
  template: `
    <div class="flex flex-row items-center gap-4">
      <div class="max-w-100">
        <image-cropper
          [imageFile]="data"
          [maintainAspectRatio]="true"
          [containWithinAspectRatio]="containWithinAspectRatio"
          [aspectRatio]="1"
          [initialStepSize]="1"
          [cropperMinHeight]="300"
          [cropperMinWidth]="300"
          [resizeToHeight]="300"
          [resizeToWidth]="300"
          [onlyScaleDown]="true"
          [roundCropper]="true"
          [canvasRotation]="canvasRotation"
          [transform]="transform"
          [alignImage]="'left'"
          [style.display]="showCropper ? null : 'none'"
          output="base64"
          format="png"
          (imageCropped)="imageCropped($event)"
          (imageLoaded)="imageLoaded()"
          (cropperReady)="onCropperReady()"
          (loadImageFailed)="loadImageFailed()" />
      </div>
      @if (showCropper) {
        <mat-icon class="icon-size-20" [svgIcon]="'feather:chevrons-right'" />
      }
      <img
        draggable="false"
        class="w-50 h-50 ring-bg-card rounded-full bg-gray-100 object-cover ring ring-offset-1"
        [src]="croppedImage"
        [style.display]="showCropper ? null : 'none'"
        alt="cropped image" />
    </div>
    @if (!cropperReady) {
      <mat-spinner diameter="200" class="m-16" />
    }
    <div mat-dialog-actions class="flex flex-row justify-end">
      <button mat-stroked-button (click)="onClose()">취소</button>
      <button
        mat-flat-button
        color="warn"
        [disabled]="imageLoadFailed || !cropperReady || !croppedImage"
        (click)="onSubmit()">
        제출
      </button>
    </div>
    @if (imageLoadFailed) {
      <div class="error-message mt-2 text-red-500">
        이미지를 로드하는 데 실패했습니다. 다른 이미지를 시도하세요.
      </div>
    }
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileImageCropperDialogComponent
  implements AfterViewInit, OnDestroy
{
  readonly hotkeyManager = injectHotkeyManagerService();
  croppedImage: any = '';
  canvasRotation = 0;
  scale = 1;
  showCropper = false;
  containWithinAspectRatio = false;
  transform: ImageTransform = {};

  cropperReady = false;
  imageLoadFailed = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: File,
    public matDialogRef: MatDialogRef<ProfileImageCropperDialogComponent>
  ) {}

  ngAfterViewInit(): void {
    this.hotkeyManager.activateHotkeysForComponent(this.constructor.name, [
      {
        keys: 'meta.enter',
        description: '저장하기',
        allowIn: allAllowInElements,
        action: () => {
          this.onSubmit();
        },
      },
      {
        keys: 'escape',
        description: '닫기',
        allowIn: allAllowInElements,
        action: () => {
          this.onClose();
        },
      },
    ]);
  }

  ngOnDestroy(): void {
    this.hotkeyManager.deactivateHotkeys(this.constructor.name);
  }

  onSubmit() {
    if (this.imageLoadFailed || !this.cropperReady) {
      console.warn('Cannot submit: Image failed to load or cropper not ready');
      return;
    }

    if (!this.croppedImage || !this.isValidBase64Image(this.croppedImage)) {
      console.warn('Cannot submit: Invalid image data');
      return;
    }

    try {
      const imageFile = base64ToFile(this.croppedImage);
      this.matDialogRef.close({
        imageFile,
        contentType: 'image/png',
      });
    } catch (error) {
      console.error('Error converting image:', error);
    }
  }

  private isValidBase64Image(base64String: string): boolean {
    if (!base64String) {
      return false;
    }

    if (!base64String.startsWith('data:image/')) {
      return false;
    }

    const base64Content = base64String.split(',')[1];
    if (!base64Content) {
      return false;
    }

    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    return base64Regex.test(base64Content);
  }

  onClose() {
    this.matDialogRef.close();
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
    this.imageLoadFailed = false; // Reset failure state on successful load
  }

  onCropperReady() {
    this.cropperReady = true;
  }

  loadImageFailed() {
    console.log('Load failed');
    this.imageLoadFailed = true;
    this.cropperReady = false;
    this.croppedImage = ''; // Reset any partially loaded image data
  }
}
